/**
 * DataPartialBanner — Bandeau honnête "X/Y modules alimentés".
 *
 * Affiche un badge ambré quand certains modules cockpit sont vides + un tooltip
 * listant les modules manquants avec liens directs vers leur configuration.
 *
 * Mêmes principes que Score.jsx (V23.13.2) : honnêteté stricte, pas de chiffre
 * fictif, CTA explicite pour combler les manques.
 *
 * Usage : <DataPartialBanner /> en haut d'Executive 360° et de Dashboard.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { CircleAlert, Plus, CircleCheck } from "lucide-react";

export default function DataPartialBanner({ compact = false }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/workspace/cockpit/data-completeness");
        if (!cancelled) setData(r.data);
      } catch {
        if (!cancelled) setData(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!data) return null;
  const empty = (data.modules || []).filter((m) => !m.has_data);

  // Plein : badge vert discret pour confirmer
  if (!data.is_partial) {
    if (compact) return null; // dans Executive on évite le bruit
    return (
      <div
        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#1D9E75] bg-[#1D9E75]/10 border border-[#1D9E75]/30 rounded px-2 py-1"
        data-testid="data-completeness-full"
      >
        <CircleCheck className="w-3 h-3" />
        {t("completeness.full", {
          count: data.modules_total,
          defaultValue: `${data.modules_total}/${data.modules_total} modules alimentés`,
        })}
      </div>
    );
  }

  return (
    <div
      className="noam-card p-3 border border-[#EF9F27]/30 bg-[#EF9F27]/[0.04]"
      data-testid="data-partial-banner"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2.5">
          <CircleAlert className="w-4 h-4 text-[#EF9F27] shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-semibold text-zinc-100">
              <span data-testid="data-partial-ratio">{data.modules_with_data}/{data.modules_total}</span>{" "}
              {t("completeness.label", { defaultValue: "modules alimentés" })}
            </div>
            <div className="text-[11px] text-zinc-400 leading-snug mt-1 max-w-xl">
              {t("completeness.hint", {
                defaultValue: "Cette vue reflète uniquement les modules contenant de la donnée réelle. Les modules vides ne contribuent pas aux indicateurs — configurez-les pour une vision complète.",
              })}
            </div>
          </div>
        </div>
        <button
          onClick={() => setExpanded((v) => !v)}
          className="btn-ghost text-xs"
          data-testid="data-partial-toggle"
        >
          {expanded
            ? t("completeness.collapse", { defaultValue: "Masquer" })
            : t("completeness.expand", { count: empty.length, defaultValue: `Voir les ${empty.length} modules vides` })}
        </button>
      </div>

      {expanded && (
        <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5" data-testid="data-partial-list">
          {empty.map((m) => (
            <Link
              key={m.key}
              to={m.setup_route}
              className="flex items-center justify-between gap-2 px-2.5 py-2 rounded border border-white/5 hover:border-white/15 hover:bg-white/[0.02] transition group"
              data-testid={`data-partial-item-${m.key}`}
            >
              <span className="text-[12px] text-zinc-300 truncate">{m.label}</span>
              <span className="text-[10px] text-[#378ADD] opacity-70 group-hover:opacity-100 flex items-center gap-1 shrink-0">
                <Plus className="w-3 h-3" />
                {t("completeness.configure", { defaultValue: "Configurer" })}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
